import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import {
  api,
  type AuditEvent,
  type SealVerification,
} from '../api/client';
import { useSession } from '../session/SessionContext';
import { AvisoError } from '../components/Aviso';
import { CambiosAuditoria } from '../components/CambiosAuditoria';
import { Modal } from '../components/Modal';
import { Paginacion } from '../components/Paginacion';
import { BuscadorDePersona } from '../components/BuscadorDePersona';
import { AccionIcono } from '../components/AccionIcono';
import { IconoVer } from '../components/Iconos';

const TONO_RESULTADO: Record<string, string> = {
  SUCCESS: 'bueno',
  DENIED: 'aviso',
  ERROR: 'critico',
};

/**
 * Lo que es una advertencia aunque «haya salido bien»: que se registrara una
 * posible captura de pantalla no es un éxito en verde, es algo que hay que ver
 * (ADR-0029). Va en ámbar y dice «Advertencia».
 */
const ACCIONES_DE_ADVERTENCIA = new Set(['viewer.capture_suspected']);

const esAdvertencia = (evento: { action: string }) => ACCIONES_DE_ADVERTENCIA.has(evento.action);
const tonoDe = (evento: { action: string; outcome: string }) =>
  esAdvertencia(evento) ? 'aviso' : TONO_RESULTADO[evento.outcome] ?? 'neutro';

/**
 * Bitácora de auditoría: qué se hizo, quién, sobre qué y con qué resultado.
 *
 * Lo que un auditor busca primero no son los éxitos sino **los intentos
 * denegados**, así que el filtro por resultado está a la vista y no escondido en
 * un menú.
 */
export function Bitacora() {
  const { t, i18n } = useTranslation();
  const { puede } = useSession();
  const [parametros, setParametros] = useSearchParams();

  const [eventos, setEventos] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [porPagina, setPorPagina] = useState(25);
  const [acciones, setAcciones] = useState<string[]>([]);
  /**
   * Identificadores resueltos a nombres, para que el detalle diga «Todo el
   * personal» y no un identificador. Se arma con los catálogos que el portal ya
   * expone; lo que no esté ahí —algo borrado desde entonces— se dice como tal.
   */
  const [nombres, setNombres] = useState<Map<string, string>>(new Map());
  const [sellos, setSellos] = useState<{
    items: SealVerification[];
    allIntact: boolean;
    checked: number;
  } | null>(null);
  const [detalle, setDetalle] = useState<AuditEvent | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [cargando, setCargando] = useState(true);

  const fechaHora = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
    timeStyle: 'medium',
  });
  const consulta = parametros.toString();

  /**
   * Cómo se cuenta lo que pasó.
   *
   * Un intento denegado **no guarda una acción, guarda el permiso que faltaba**:
   * `document.publish` denegado significa que alguien quiso publicar, no que
   * publicó. Traducirlo con la misma frase decía justo lo contrario de lo que
   * ocurrió, así que el resultado decide qué diccionario se usa.
   */
  const enPalabras = useCallback(
    (accion: string, resultado: string) =>
      resultado === 'SUCCESS'
        ? t(`audit.actions.${accion}`, {
            defaultValue: t('audit.unknownAction', { code: accion }),
          })
        : t(`audit.attempts.${accion}`, {
            defaultValue: t(`audit.actions.${accion}`, {
              defaultValue: t('audit.unknownAction', { code: accion }),
            }),
          }),
    [t],
  );

  const fijar = useCallback(
    (clave: string, valor: string) => {
      const siguientes = new URLSearchParams(parametros);
      if (valor) siguientes.set(clave, valor);
      else siguientes.delete(clave);
      siguientes.delete('page');
      setParametros(siguientes, { replace: true });
    },
    [parametros, setParametros],
  );

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    api
      .audit(consulta)
      .then((pagina) => {
        if (!vigente) return;
        setEventos(pagina.items);
        setTotal(pagina.totalItems);
        setPorPagina(pagina.pageSize);
        setError(null);
      })
      .catch((e) => vigente && setError(e))
      .finally(() => vigente && setCargando(false));
    return () => {
      vigente = false;
    };
  }, [consulta]);

  useEffect(() => {
    api.auditActions().then(setAcciones).catch(setError);
    api.auditSeals().then(setSellos).catch(setError);

    Promise.all([api.groups(), api.categories(true), api.departments(), api.roles()])
      .then(([grupos, categorias, areas, roles]) => {
        const mapa = new Map<string, string>();
        for (const lista of [grupos, categorias, areas, roles]) {
          for (const elemento of lista) mapa.set(elemento.id, elemento.name);
        }
        setNombres(mapa);
      })
      .catch(setError);

    if (puede('user.manage')) {
      // Los nombres del detalle siguen haciendo falta para resolver
      // identificadores, aunque el filtro ya no use una lista completa.
      api
        .users('?pageSize=100')
        .then((pagina) =>
          setNombres((previos) => {
            const mapa = new Map(previos);
            for (const usuario of pagina.items) mapa.set(usuario.id, usuario.fullName);
            return mapa;
          }),
        )
        .catch(setError);
    }
  }, [puede]);

  return (
    <>
      <div className="pagina__encabezado">
        <h1 className="pagina__titulo">{t('audit.title')}</h1>
        <p className="pagina__bajada">{t('audit.subtitle')}</p>
      </div>

      <p className="pagina__ayuda">{t('audit.help')}</p>

      <AvisoError error={error} />

      {/* El estado de la cadena de sellos va arriba: si algo no cuadra, es lo
          primero que hay que saber antes de leer una sola línea. El color lo
          dice antes de leer: verde cuando cuadra, rojo cuando no, ámbar cuando
          todavía no hay nada que verificar — con ámbar para el caso bueno,
          «todo en orden» se leía como advertencia. */}
      {sellos && (
        <p
          className={`aviso aviso--${
            sellos.checked === 0 ? 'info' : sellos.allIntact ? 'exito' : 'error'
          }`}
        >
          {sellos.checked === 0
            ? t('audit.noSeals')
            : sellos.allIntact
              ? t('audit.sealsIntact', { count: sellos.checked })
              : t('audit.sealsBroken', {
                  dias: sellos.items
                    .filter((s) => !s.intact || !s.chained)
                    .map((s) => s.sealDate.slice(0, 10))
                    .join(', '),
                })}
        </p>
      )}

      <section className="tarjeta">
        <div className="tarjeta__encabezado">
          <h2 className="tarjeta__titulo">{t('audit.events')}</h2>
          <span className="tarjeta__nota">
            {cargando ? t('documents.searching') : t('audit.count', { count: total })}
          </span>
          {puede('audit.export') && (
            <a className="boton boton--discreto" href={`/api/v1/audit/export?${consulta}`}>
              {t('access.export')}
            </a>
          )}
        </div>

        <div className="filtros">
          <div className="campo">
            <label htmlFor="action">{t('audit.action')}</label>
            <select
              id="action"
              value={parametros.get('action') ?? ''}
              onChange={(e) => fijar('action', e.target.value)}
            >
              <option value="">{t('documents.filterAll')}</option>
              {[...acciones]
                .map((accion) => ({ accion, etiqueta: enPalabras(accion, 'SUCCESS') }))
                .sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, i18n.language))
                .map(({ accion, etiqueta }) => (
                  <option key={accion} value={accion}>
                    {etiqueta}
                  </option>
                ))}
            </select>
          </div>

          <div className="campo">
            <label htmlFor="outcome">{t('audit.outcome')}</label>
            <select
              id="outcome"
              value={parametros.get('outcome') ?? ''}
              onChange={(e) => fijar('outcome', e.target.value)}
            >
              <option value="">{t('documents.filterAll')}</option>
              {(['SUCCESS', 'DENIED', 'ERROR'] as const).map((resultado) => (
                <option key={resultado} value={resultado}>
                  {t(`audit.result.${resultado}`)}
                </option>
              ))}
            </select>
          </div>

          {puede('user.manage') && (
            <BuscadorDePersona
              etiqueta={t('audit.actor')}
              valor={parametros.get('actorUserId') ?? ''}
              onElegir={(userId) => fijar('actorUserId', userId)}
            />
          )}

          <div className="campo">
            <label htmlFor="from">{t('access.from')}</label>
            <input
              id="from"
              type="date"
              value={parametros.get('from')?.slice(0, 10) ?? ''}
              onChange={(e) => fijar('from', e.target.value)}
            />
          </div>

          <div className="campo">
            <label htmlFor="to">{t('access.to')}</label>
            <input
              id="to"
              type="date"
              value={parametros.get('to')?.slice(0, 10) ?? ''}
              onChange={(e) => fijar('to', e.target.value)}
            />
          </div>
        </div>

        {eventos.length === 0 ? (
          <div className="vacio">
            <span className="vacio__titulo">{t('audit.empty')}</span>
            <span>{t('access.emptyHint')}</span>
          </div>
        ) : (
          <div className="tabla-envoltura">
            <table>
              <thead>
                <tr>
                  <th>{t('audit.when')}</th>
                  <th>{t('audit.who')}</th>
                  <th>{t('audit.whatHappened')}</th>
                  <th>{t('audit.onWhat')}</th>
                  <th>{t('audit.outcome')}</th>
                  <th>{t('audit.fromWhere')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {eventos.map((evento) => (
                  <tr key={evento.id} className={esAdvertencia(evento) ? 'fila--advertencia' : undefined}>
                    <td className="mono">{fechaHora.format(new Date(evento.occurredAt))}</td>
                    <td>
                      {evento.actorUsername === 'SYSTEM' || !evento.actorUsername
                        ? t('audit.systemActor')
                        : evento.actorUsername}
                    </td>
                    {/* La acción en una frase, no el código. Si algún día llega
                        uno sin traducir, se muestra el código en vez de una
                        celda vacía: mejor críptico que mudo. */}
                    <td>{enPalabras(evento.action, evento.outcome)}</td>
                    {/* Sobre qué, en palabras: qué documento se abrió o se cargó,
                        a qué persona se le cambió algo. Antes decía sólo
                        «Documento» y había que buscar el identificador. */}
                    <td>
                      <SobreQue evento={evento} />
                    </td>
                    <td>
                      <span className={`pildora pildora--${tonoDe(evento)}`}>
                        <span className="pildora__punto" aria-hidden="true" />
                        {esAdvertencia(evento) ? t('audit.result.WARNING') : t(`audit.result.${evento.outcome}`)}
                      </span>
                      {evento.reason && (
                        <>
                          <br />
                          <span className="tenue" style={{ fontSize: '.75rem' }}>
                            {t(`audit.reasons.${evento.reason}`, { defaultValue: evento.reason })}
                          </span>
                        </>
                      )}
                    </td>
                    <td className="mono">{evento.ipAddress ?? '—'}</td>
                    <td>
                      <AccionIcono
                        etiqueta={t('audit.detail')}
                        icono={<IconoVer />}
                        onClick={() => setDetalle(evento)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Paginacion total={total} porPagina={porPagina} cargando={cargando} />
      </section>

      {detalle && (
        <Modal
          titulo={enPalabras(detalle.action, detalle.outcome)}
          onCerrar={() => setDetalle(null)}
        >
          <div className="datos">
            <div className="dato">
              <span className="dato__etiqueta">{t('audit.when')}</span>
              <span className="dato__valor dato__valor--mono">
                {fechaHora.format(new Date(detalle.occurredAt))}
              </span>
            </div>
            <div className="dato">
              <span className="dato__etiqueta">{t('audit.who')}</span>
              <span className="dato__valor">
                {detalle.actorUsername === 'SYSTEM' || !detalle.actorUsername
                  ? t('audit.systemActor')
                  : detalle.actorUsername}
              </span>
            </div>
            <div className="dato">
              <span className="dato__etiqueta">{t('audit.onWhat')}</span>
              <span className="dato__valor">
                <SobreQue evento={detalle} />
              </span>
            </div>
            <div className="dato">
              <span className="dato__etiqueta">{t('audit.fromWhere')}</span>
              <span className="dato__valor dato__valor--mono">{detalle.ipAddress ?? '—'}</span>
            </div>
            {detalle.reason && (
              <div className="dato">
                <span className="dato__etiqueta">{t('audit.fields.reason')}</span>
                <span className="dato__valor">
                  {t(`audit.reasons.${detalle.reason}`, { defaultValue: detalle.reason })}
                </span>
              </div>
            )}
            {/* El código técnico se conserva, discreto: quien tenga que abrir un
                reporte con soporte lo necesita, y quien no, no tropieza con él. */}
            <div className="dato">
              <span className="dato__etiqueta">{t('audit.technicalCode')}</span>
              <span className="dato__valor dato__valor--mono">{detalle.action}</span>
            </div>
            <div className="dato">
              <span className="dato__etiqueta">{t('audit.outcome')}</span>
              <span className="dato__valor">
                <span
                  className={`pildora pildora--${tonoDe(detalle)}`}
                >
                  <span className="pildora__punto" aria-hidden="true" />
                  {esAdvertencia(detalle) ? t('audit.result.WARNING') : t(`audit.result.${detalle.outcome}`)}
                </span>
              </span>
            </div>
          </div>

          <h3 className="modal__seccion">{t('audit.changesTitle')}</h3>
          <CambiosAuditoria
            anterior={detalle.previousValue}
            nuevo={detalle.newValue}
            nombres={nombresDelEvento(nombres, detalle)}
          />
        </Modal>
      )}
    </>
  );
}

/**
 * El objeto de un evento: el título del documento —con su clave y un enlace— o
 * el usuario de la persona. Si no hay nombre que dar, el tipo de objeto.
 */
/**
 * Los nombres de siempre más los que trae el propio evento: su objeto —un
 * documento no está en las listas que se cargan— y la versión cuando guardó su
 * etiqueta. Sin esto, el detalle decía «un elemento que ya no existe» de un
 * documento que estaba ahí.
 */
function nombresDelEvento(nombres: Map<string, string>, evento: AuditEvent): Map<string, string> {
  const mapa = new Map(nombres);
  if (evento.targetId && evento.targetLabel) mapa.set(evento.targetId, evento.targetLabel);
  const nuevo = evento.newValue as Record<string, unknown> | null | undefined;
  if (typeof nuevo?.['versionId'] === 'string' && typeof nuevo['versionLabel'] === 'string') {
    mapa.set(nuevo['versionId'], nuevo['versionLabel']);
  }
  return mapa;
}

function SobreQue({ evento }: { evento: AuditEvent }) {
  const { t } = useTranslation();
  const tipo = evento.targetType
    ? t(`audit.targets.${evento.targetType}`, { defaultValue: evento.targetType })
    : '—';
  // La fila guarda la clave del sistema y no se toca; el código de la caja se
  // resuelve al consultar, igual que el título.
  const clave = evento.targetReferenceCode || evento.targetCode;

  if (evento.targetType === 'document' && evento.targetLabel && evento.targetId) {
    return (
      <span className="bitacora-objeto">
        <Link to={`/documentos/${evento.targetId}`}>{evento.targetLabel}</Link>
        {clave && <span className="campo__ayuda mono">{clave}</span>}
      </span>
    );
  }
  if (evento.targetLabel) {
    return (
      <span className="bitacora-objeto">
        <span>{evento.targetLabel}</span>
        <span className="campo__ayuda">{tipo}</span>
      </span>
    );
  }
  return <>{clave || tipo}</>;
}
